// ============================= PREDICTIVE SEARCH COMPONENT =============================
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="text"]');
    this.predictiveSearchResults = document.querySelector('#predictive-search');
    this.abortController = new AbortController();

    if (!this.input || !this.predictiveSearchResults) return;

    this.onInput = this.debounce((event) => {
      this.onChange(event);
      this.toggleButtons(this.input.value.length > 0);
    }, 300).bind(this);

    this.input.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    if (this.input) {
      this.input.removeEventListener('input', this.onInput);
    }
    this.abortController.abort();
  }

  toggleButtons(enable) {
    document.querySelectorAll('.search-Button').forEach(btn => {
      btn.disabled = !enable;
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    if (!searchTerm.length) {
      this.close();
      return;
    }
    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    // 1. Get Base URL
    let baseUrl = window.routes && window.routes.predictive_search_url
      ? window.routes.predictive_search_url
      : '/search/suggest';

    const searchUrl = `${baseUrl}?q=${encodeURIComponent(searchTerm)}&resources[type]=article,page&resources[limit]=6&section_id=predictive-search`;

    this.abortController.abort();
    this.abortController = new AbortController();

    fetch(searchUrl, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const resultsSection = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!resultsSection || !this.predictiveSearchResults) {
          this.close();
          return;
        }

        this.predictiveSearchResults.innerHTML = resultsSection.innerHTML;
        this.open();

        if (window.initBlurImages) window.initBlurImages();

        // Check strictly for function existence before calling
        if (typeof window.quickAddButton === 'function') {
          // Legacy support if specific initialization is needed, 
          // though delegation handles the click.
          window.quickAddButton();
        }

        const searchValueObj = document.getElementById('SearchValue');
        if (searchValueObj) searchValueObj.value = searchTerm;
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          this.close();
        }
      });
  }

  open() {
    if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'block';
  }

  close() {
    if (this.predictiveSearchResults) this.predictiveSearchResults.style.display = 'none';
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
customElements.define('predictive-search', PredictiveSearch);


// ============================= UI HELPERS =============================

function closeSearchOverlay() {
  const body = document.body;
  const searchResult = document.querySelector('#predictive-search');
  const whiteHeader = document.querySelector('.white-page .section-header');
  const darkHeader = document.querySelector('.dark-page .section-header');

  body.classList.remove('search-open');

  if (whiteHeader) {
    whiteHeader.classList.remove('dark-header-hover');
  } else if (darkHeader) {
    darkHeader.classList.add('dark-header');
  }

  if (searchResult) searchResult.style.display = "none";
}

function closeMobileSearch() {
  const body = document.body;
  const searchResult = document.querySelector('#predictive-search');
  body.classList.remove('open-search');
  if (searchResult) searchResult.style.display = "none";
}

function closeMegaMenu() {
  const body = document.body;
  body.classList.remove('open-nav');

  const megamenu = document.querySelector('.megamenu.open-mega');
  if (megamenu) megamenu.classList.remove('open-mega');

  const megaList = document.querySelector('.mega-list.active');
  if (megaList) megaList.classList.remove('active');

  const sectionHeader = document.querySelector('.section-header.dark-header-hover');
  if (sectionHeader) sectionHeader.classList.remove('dark-header-hover');

  if (body.classList.contains("dark-page")) {
    setTimeout(() => {
      const header = sectionHeader || document.querySelector('.section-header');
      if (header) header.classList.add('dark-header');
    }, 10);
  }
}

function closeDropdowns() {
  document.querySelectorAll('.main-bottom-menu .dropdown-toggle').forEach(toggle => {
    toggle.classList.remove('open');
    toggle.style.opacity = '1';
    const dropdown = toggle.nextElementSibling;
    if (dropdown) dropdown.classList.remove('show');
  });
  document.body.classList.remove('popupopen');
}


// ============================= EVENT DELEGATION HANDLERS =============================

// 1. Menu Toggles
const handleMenuToggle = (target, body) => {
  const menuToggle = target.closest('.menu-burdger, .close-menu');
  if (menuToggle) {
    body.classList.toggle('open-menu');
    body.classList.remove('open-drawer', 'open-search', 'open-sort', 'open-filter');
    return true;
  }
  return false;
};

// 2. Mobile Search
const handleMobileSearch = (target, body) => {
  const btn = target.closest('.mobile-search');
  if (btn) {
    if (body.classList.contains('open-search')) {
      closeMobileSearch();
    } else {
      body.classList.add('open-search');
      body.classList.remove('open-drawer', 'open-menu', 'open-sort', 'open-filter');
      const input = document.querySelector('.mobile-serachbox #Search');
      if (input) input.focus();
    }
    return true;
  }
  return false;
};

// 3. Header Search Icon
const handleHeaderSearch = (target, body, event) => {
  const btn = target.closest('.header-icons li.search');
  if (btn) {
    event.preventDefault();
    body.classList.add('search-open');
    body.classList.remove('open-nav');

    const openMega = document.querySelector('.megamenu.open-mega');
    if (openMega) openMega.classList.remove('open-mega');
    const activeMega = document.querySelector('.mega-list.active');
    if (activeMega) activeMega.classList.remove('active');

    document.querySelectorAll('.section-header').forEach(header => {
      header.classList.remove('dark-header', 'dark-header-hover');
    });
    return true;
  }
  return false;
};

// 4. Mega Menu Logic (Desktop mainly)
const handleMegaMenu = (target, body, event) => {
  const megaLink = target.closest('.list-menu .mega-list > a');
  if (megaLink) {
    event.preventDefault();
    const megaList = megaLink.closest('.mega-list');
    const megaMenu = megaLink.nextElementSibling;

    if (megaMenu) megaMenu.classList.toggle('open-mega');

    if (megaList) {
      megaList.classList.toggle('active');
      if (megaList.parentElement) {
        megaList.parentElement.querySelectorAll(':scope > .mega-list').forEach(sibling => {
          if (sibling !== megaList) {
            sibling.classList.remove('active');
            const siblingMegaMenu = sibling.querySelector('.megamenu');
            if (siblingMegaMenu) siblingMegaMenu.classList.remove('open-mega');
          }
        });
      }

      if (megaList.classList.contains('active')) {
        body.classList.add('open-nav');
        body.classList.remove('open-menu', 'open-drawer', 'open-search', 'open-sort', 'open-filter');
      } else {
        body.classList.remove('open-nav');
      }

      const sectionHeader = megaList.closest('.section-header');
      if (megaList.classList.contains('dark-mega-list')) {
        if (sectionHeader) sectionHeader.classList.toggle('dark-header-hover');
      } else if (sectionHeader) {
        sectionHeader.classList.remove('dark-header-hover');
      }

      if (body.classList.contains('dark-page') && megaList.classList.contains('white-mega-list') && sectionHeader) {
        megaList.classList.contains('active') ? sectionHeader.classList.add('dark-header') : sectionHeader.classList.remove('dark-header');
      }
    }

    // Header Right/Left Logic
    const headerRow = megaLink.closest('.header-row');
    const headerRight = megaLink.closest('.header-right');

    if (headerRow) {
      if (headerRight) {
        headerRight.classList.toggle('padding');
        const leftList = headerRow.querySelector('.header-left .mega-list');
        if (leftList) leftList.classList.remove('active');
        const leftMenu = headerRow.querySelector('.header-left .mega-list .megamenu');
        if (leftMenu) leftMenu.classList.remove('open-mega');
      } else {
        // Is Header Left
        const rightContainer = headerRow.querySelector('.header-right');
        if (rightContainer) rightContainer.classList.remove('padding');
        const rightList = headerRow.querySelector('.header-right .mega-list');
        if (rightList) rightList.classList.remove('active');
        const rightMenu = headerRow.querySelector('.header-right .mega-list .megamenu');
        if (rightMenu) rightMenu.classList.remove('open-mega');
      }
    }
    return true;
  }
  return false;
};

// ============================= MAIN CLICK LISTENER =============================
document.addEventListener('click', function (event) {
  if (!(event.target instanceof Element)) return;
  const target = event.target;
  const body = document.body;
  if (!body) return;

  // Execute handlers. 

  if (handleMenuToggle(target, body)) return;
  if (handleMobileSearch(target, body)) return;
  if (handleHeaderSearch(target, body, event)) return;
  if (handleMegaMenu(target, body, event)) return;

  // Overlay
  if (target.closest('.overlay-box')) {
    body.classList.remove('open-menu', 'open-view', 'open-popup');
    return;
  }

  // Nav Arrows (Mobile Submenu Toggle)
  // FIX: Do NOT return here. Allow event to bubble so "Mobile Nav First" logic below can also run if needed.
  const navArrow = target.closest('.nav-arrow');
  if (navArrow) {
    const navItem = navArrow.closest('li');
    if (navItem) navItem.classList.toggle('active');
    // Removed "return" to fix mobile menu regression
  }

  // Search Close
  if (target.closest('.search-close .close')) {
    closeSearchOverlay();
    return;
  }

  // Mobile Nav First (Slide animation logic)
  const mobileNavFirst = target.closest('.mobile-nav .first');
  if (mobileNavFirst) {
    const method = mobileNavFirst.classList.contains('active') ? 'add' : 'remove';
    mobileNavFirst.classList[method]('hide');
    if (mobileNavFirst.parentElement) {
      mobileNavFirst.parentElement.querySelectorAll(':scope > *').forEach(sib => {
        if (sib !== mobileNavFirst) sib.classList[method]('hide');
      });
    }
    // No return here either, safer to let it complete
  }

  // Mobile Back Btn
  if (target.closest('.back-btn .click-btn')) {
    const parentLi = target.closest('li.first');
    if (parentLi) parentLi.classList.remove('active');

    const parentFirst = target.closest('.mobile-nav .first');
    if (parentFirst && parentFirst.parentElement) {
      parentFirst.classList.remove('hide');
      parentFirst.parentElement.querySelectorAll(':scope > *').forEach(sib => {
        sib.classList.remove('hide');
      });
    }

    const predictiveSearch = document.querySelector('#predictive-search');
    if (predictiveSearch) predictiveSearch.style.display = 'none';

    const searchMob = document.getElementById('search-mob');
    if (searchMob) searchMob.value = '';
    return;
  }

  // Mega Menu Close
  if (target.closest('.megamenu .megamenu-close')) {
    closeMegaMenu();
    return;
  }

  // Bottom Menu Dropdown
  const bottomToggle = target.closest('.main-bottom-menu .dropdown-toggle');
  if (bottomToggle) {
    event.preventDefault();
    document.querySelectorAll('.main-bottom-menu .dropdown-toggle').forEach(other => {
      if (other !== bottomToggle) {
        other.classList.remove('open');
        other.style.opacity = '0.5';
        const otherDropdown = other.nextElementSibling;
        if (otherDropdown) otherDropdown.classList.remove('show');
      }
    });

    const dropdown = bottomToggle.nextElementSibling;
    if (dropdown) {
      dropdown.classList.toggle('show');
      bottomToggle.classList.toggle('open');
      bottomToggle.style.opacity = '1';
      dropdown.querySelectorAll("a").forEach(link => link.style.opacity = '1');
      body.classList.add('popupopen');
    }
    return;
  }

  // Close Bottom Dropdowns when clicking outside
  const bottomMenu = document.querySelector('.main-bottom-menu');
  if (bottomMenu && !bottomMenu.contains(target)) {
    closeDropdowns();
  }

  // Close Search Overlay when clicking outside
  if (body.classList.contains('search-open')) {
    if (!target.closest('.searchlist, .mobile-serachbox, #predictive-search, .search-close, .header-icons li.search')) {
      closeSearchOverlay();
    }
  }

  if (body.classList.contains('open-search')) {
    if (!target.closest('.mobile-serachbox, .mobile-search, #predictive-search')) {
      closeMobileSearch();
    }
  }

  if (body.classList.contains('open-nav')) {
    if (!target.closest('.header-menu, .megamenu, .list-menu')) {
      closeMegaMenu();
    }
  }
});


// ============================= GLOBAL HELPERS =============================

// Compatibility: Function is empty because logic is handled by global delegation above.
// This prevents errors if other scripts try to call it.
window.quickAddButton = function () { };

// Global Submit Toggle
window.toggleSubmit = () => {
  const submit = document.querySelector('.register-submit-button');
  if (submit) submit.disabled = !submit.disabled;
};


// ============================= KEYBOARD & SCROLL HANDLERS =============================

document.onkeydown = function (evt) {
  evt = evt || window.event;
  const key = evt.key || evt.keyCode;
  const isEscape = (key === "Escape" || key === "Esc" || key === 27);

  if (isEscape) {
    const body = document.body;
    if (!body) return;

    body.classList.remove('open-filter', 'open-sort', 'open-drawer', 'open-menu', 'open-search', 'search-open', 'open-nav', 'open-view', 'open-popup', 'open-account-popup', 'popupopen');

    closeSearchOverlay();
    closeMobileSearch();
    closeMegaMenu();
    closeDropdowns();

    document.querySelectorAll('.main-acc-popup').forEach(p => p.classList.remove('active'));
    document.querySelectorAll('.header-icons li.userlist').forEach(u => u.classList.remove('active'));
  }
};

let lastScroll = 0;
let headerScrollTicking = false;
window.addEventListener("scroll", function () {
  if (headerScrollTicking) return;
  headerScrollTicking = true;
  const scroll = window.scrollY || window.pageYOffset;

  setTimeout(function () {
    const body = document.body;
    if (scroll <= 40) {
      body.classList.remove('scroll-up', 'scroll-down');
    } else if (scroll > lastScroll) {
      body.classList.remove('scroll-up');
      body.classList.add('scroll-down');
    } else if (scroll < lastScroll) {
      body.classList.remove('scroll-down');
      body.classList.add('scroll-up');
    }
    lastScroll = scroll;
    headerScrollTicking = false;
  }, 100);
}, { passive: true });


// ============================= INTERSECTION OBSERVER (IMAGE BLUR) =============================

// Define Observer once
const blurObserver = new IntersectionObserver((entries, observer) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('animation-done');
      observer.unobserve(entry.target);
    }
  });
}, { rootMargin: '0px 0px 100px 0px' }); // Load slighty before view

// Public function to re-init observer (useful for ajax)
window.initBlurImages = () => {
  document.querySelectorAll('.blur-img:not(.animation-done)').forEach(img => {
    blurObserver.observe(img);
  });
};

document.addEventListener('DOMContentLoaded', window.initBlurImages);
// Also trigger on change just in case specific theme logic fires it
document.addEventListener('change', () => setTimeout(window.initBlurImages, 1500));


// ============================= MISC UI HANDLERS =============================

// Video Controls
document.querySelectorAll('.video-icon-inr, .video-icon-inr-mob').forEach((videoControl) => {
  videoControl.addEventListener('click', function (e) {
    e.preventDefault();
    this.classList.toggle('active');

    const mediaWrap = this.closest('.media-item-wrap');
    const isMob = this.classList.contains('video-icon-inr-mob');
    const video = mediaWrap ? mediaWrap.querySelector(isMob ? 'video.mob-video' : 'video') : null;

    if (!video) return;

    if (this.getAttribute('data-val') == 'play') {
      this.classList.contains('active') ? video.play() : video.pause();
    } else {
      video.muted = !this.classList.contains('active');
    }
  });
});

// Account Popup Toggles
if (document.querySelector('.enable-forgot.popup')) {
  const loginForm = document.querySelector('.main-acc-popup .login-form');
  const recoverForm = document.querySelector('.main-acc-popup .recover-form');
  const forgotBtn = document.querySelector('.enable-forgot.popup');

  if (forgotBtn && loginForm && recoverForm) {
    forgotBtn.addEventListener('click', (e) => {
      e.preventDefault();
      loginForm.classList.add('hide-form');
      recoverForm.classList.remove('hide-form');
    });
  }
}

// Collection View Toggles
const collProdView = document.querySelector('.coll-prod-view');
if (collProdView) {
  collProdView.addEventListener('click', function (e) {
    e.preventDefault();
    const productGrid = document.querySelector('#product-grid');
    const modelView = document.querySelector('.coll-model-view');

    if (modelView) modelView.closest('li').classList.remove('active');
    this.closest('li').classList.add('active');

    if (productGrid) productGrid.classList.add('packshot-grid');
    document.body.classList.remove("open-view");
  });
}

const collModelView = document.querySelector('.coll-model-view');
if (collModelView) {
  collModelView.addEventListener('click', function (e) {
    e.preventDefault();
    const productGrid = document.querySelector('#product-grid');
    const prodView = document.querySelector('.coll-prod-view');

    if (prodView) prodView.closest('li').classList.remove('active');
    this.closest('li').classList.add('active');

    if (productGrid) productGrid.classList.remove('packshot-grid');
    sessionStorage.setItem("col-view", "model");
    document.body.classList.remove("open-view");
  });
}

// Helper for Account Forms
function accountpopup() {
  const loginForm = document.querySelector('.main-acc-popup .login-form');
  const recoverForm = document.querySelector('.main-acc-popup .recover-form');
  const mainLogin = document.querySelector('.customer.login');
  const mainReg = document.querySelector('.customer.register');

  const backLogin = document.querySelector('.enable-login.back');
  if (backLogin && loginForm) {
    backLogin.addEventListener('click', (e) => {
      e.preventDefault();
      loginForm.classList.remove('hide-form');
      recoverForm.classList.add('hide-form');
    });
  }

  const createAcc = document.querySelector('.customer .creat-account');
  if (createAcc && mainLogin) {
    createAcc.addEventListener('click', (e) => {
      e.preventDefault();
      mainLogin.classList.add('hide-form');
      mainReg.classList.remove('hide-form');
    });
  }

  const haveAcc = document.querySelector('.have-acc.back');
  if (haveAcc && mainLogin) {
    haveAcc.addEventListener('click', (e) => {
      e.preventDefault();
      mainLogin.classList.remove('hide-form');
      mainReg.classList.add('hide-form');
    });
  }
}
accountpopup();

// Header Search Auto-Focus
const headerSearchLink = document.querySelector('.header-icons .search a');
if (headerSearchLink) {
  headerSearchLink.addEventListener('click', () => {
    setTimeout(() => {
      const searchInput = document.querySelector('.snippet-search-bar input#Search');
      if (searchInput) searchInput.focus();
    }, 200);
  });
}

// Fetch Account HTML
const accRegisterUrl = window.routes && window.routes.account_register_url
  ? window.routes.account_register_url
  : '/account/register';

fetch(accRegisterUrl, { credentials: 'same-origin' })
  .then(res => res.ok ? res.text() : Promise.reject(res.status))
  .then(text => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');
    const countryId = doc.getElementById('account_popup_html');
    const container = document.querySelector('.main-acc-popup');

    if (countryId && container) {
      container.innerHTML = countryId.innerHTML;
      accountpopup();
    }
  })
  .catch(() => { });

// Banner Click
const collectionBanner = document.querySelector('.main-collection-banner');
if (collectionBanner) {
  collectionBanner.addEventListener('click', function (e) {
    if (e.target.classList.contains('main-collection-banner')) {
      this.classList.add('active');
    }
  });
}

// Account Popup Trigger
if (document.querySelectorAll('.main-acc-popup').length > 0) {
  document.querySelectorAll('.header-icons li.userlist,.user_btn').forEach(function (element) {
    element.addEventListener('click', function (event) {
      event.preventDefault();
      if (document.body.classList.contains('open-menu')) {
        document.body.classList.remove('open-menu');
      }
      element.classList.add('active');
      document.querySelectorAll('.main-acc-popup').forEach(function (popup) {
        popup.classList.add('active');
      });
      document.body.classList.add('open-account-popup');
    });
  });
}

// Account Popup Close
if (document.querySelectorAll('.main-acc-popup').length > 0) {
  document.addEventListener('click', function (event) {
    if (event.target.classList.contains('overlay-box') && event.target.closest('.open-account-popup')) {
      document.querySelectorAll('.main-acc-popup').forEach(function (popup) {
        popup.classList.remove('active');
      });
      document.querySelectorAll('.header-icons li.userlist').forEach(function (userlist) {
        userlist.classList.remove('active');
      });
      document.body.classList.remove('open-account-popup');
    }
    if (event.target.closest('.acc-popup-close')) {
      document.querySelectorAll('.main-acc-popup').forEach(function (popup) {
        popup.classList.remove('active');
      });
      document.querySelectorAll('.header-icons li.userlist').forEach(function (userlist) {
        userlist.classList.remove('active');
      });
      document.body.classList.remove('open-account-popup');
    }
  });
}

// ============================= RESPONSIVE LOGIC =============================

// Desktop Logic (>991px)
if (window.innerWidth > 991) {
  document.querySelectorAll('.pro-grid-filter li').forEach(el => {
    el.addEventListener('click', function () {
      const val = this.getAttribute('data-id');
      if (val === 'view-type-model') {
        document.body.classList.remove('view-type-packshot');
        document.body.classList.add('view-type-model');
      }
      sessionStorage.setItem('ModelView', val);
    });
  });

  const savedView = sessionStorage.getItem('ModelView');
  if (savedView) {
    document.body.classList.add(savedView);
    if (savedView === 'view-type-packshot') {
      const btn = document.querySelector('.coll-prod-view');
      if (btn) btn.click();
    }
  }

  // Split Banner
  document.querySelectorAll('.split-banner .media-list').forEach(function (element) {
    const titleEl = element.querySelector('.media-caption-inner .media-top .title');
    const mediaTop = element.querySelector('.media-caption-inner .media-top');
    if (!titleEl || !mediaTop) return;
    const mHeight = titleEl.offsetHeight;
    mediaTop.style.transform = 'translateY(calc(100% - ' + mHeight + 'px))';
  });
}

// Mobile Logic (<991px)
if (window.matchMedia('(max-width: 991px)').matches) {
  const mobileArrows = document.querySelectorAll('.arrow-icon');
  if (mobileArrows) {
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('arrow-icon')) {
        e.preventDefault();
        e.target.closest('.btn').classList.toggle('open');
      }
    });
  }

  // Move Filters
  const filter = document.querySelector('.snippet-collection-filter');
  const announce = document.querySelector('.header_announcement');
  if (filter && announce && announce.parentNode) {
    announce.parentNode.insertBefore(filter, announce);
  }

  // Filter Accordions
  document.querySelectorAll('.filter-content').forEach(el => el.style.display = 'none');

  document.querySelectorAll('.collapsible').forEach(coll => {
    coll.addEventListener('click', function () {
      const content = this.nextElementSibling;
      if (content) {
        if (content.style.display === 'block') {
          content.style.display = 'none';
          const arrow = this.querySelector('.filter-arrow');
          if (arrow) arrow.classList.remove('open');
        } else {
          // Close others
          document.querySelectorAll('.filter-content').forEach(c => c.style.display = 'none');
          document.querySelectorAll('.filter-arrow').forEach(a => a.classList.remove('open'));

          content.style.display = 'block';
          const arrow = this.querySelector('.filter-arrow');
          if (arrow) arrow.classList.add('open');
        }
      }
    });
  });

  // Filter Count Updates
  const updateActiveValues = () => {
    document.querySelectorAll('.filter-content').forEach(content => {
      const count = content.querySelectorAll('.filter-count:checked').length;
      const label = content.previousElementSibling ? content.previousElementSibling.querySelector('[id^="active-filters-"]') : null;
      if (label) {
        label.textContent = `(${count})`;
        label.style.display = count === 0 ? 'none' : 'inline';
      }
    });
  };

  window.addEventListener('DOMContentLoaded', updateActiveValues);
  document.addEventListener('ajaxComplete', updateActiveValues);
  document.querySelectorAll('.filter-count').forEach(el => {
    el.addEventListener('change', updateActiveValues);
  });

  // Mobile Custom Search Listeners (Ensured attachment)
  document.querySelectorAll('.cust-mobile-search').forEach(function (element) {
    element.addEventListener('click', handleSearchEvent);
    element.addEventListener('touchend', handleSearchEvent);
    element.addEventListener('touchstart', handleSearchEvent);
  });
}

// Helper for cust-mobile-search
function handleSearchEvent(event) {
  if (!event.target) return;
  const element = event.target;
  element.classList.add('active', 'hide');
  const siblings = element.parentNode ? element.parentNode.children : null;
  if (!siblings) return;
  for (let i = 0; i < siblings.length; i++) {
    if (siblings[i] !== element) siblings[i].classList.add('hide');
  }
  const searchInput = document.getElementById('search-mob');
  if (searchInput) searchInput.focus();
}

// Header Announcement Animation
const headerAnnouncement = document.querySelector('.header_announcement');
if (headerAnnouncement) {
  headerAnnouncement.classList.add('transition');
  setTimeout(() => headerAnnouncement.classList.remove('transition'), 3000);
}

// Timeline
const mainTimeline = document.querySelector('.main-timeline.swiper-wrapper');
if (mainTimeline) mainTimeline.style.transform = 'unset';

// Mega Menu Image Swap
document.querySelectorAll('.mega-nav .mega-listmenu li a').forEach(function (anchor) {
  anchor.addEventListener('mouseover', function (event) {
    const img = this.parentElement.getAttribute("data-image");
    const sub_text = this.parentElement.getAttribute("data-sub-text");
    const megaLeft = this.closest('.mega-left');
    const megaRight = megaLeft ? megaLeft.nextElementSibling : null;
    if (!megaRight) return;

    const megaImage = megaRight.querySelector('img');
    if (megaImage) megaImage.setAttribute("src", img);

    const megaSubtitle = megaRight.querySelector('.mega-img-info .sub-title');
    if (megaSubtitle) megaSubtitle.innerHTML = sub_text;
  });
});


// ============================= COUNTRY & THIRD PARTY INTEGRATIONS =============================

document.addEventListener('DOMContentLoaded', () => {
  const code = document.body.getAttribute('data-countrycode');
  if (code) {
    setTimeout(() => {
      // Badges
      document.querySelectorAll('.grid-badge span, .grid-badge-pdp span').forEach(span => {
        const iso = span.getAttribute('data-isocode');
        span.style.display = (!iso || iso.includes(code)) ? 'none' : 'block';
        if (!iso) span.style.display = 'block';
      });

      // Upsells
      const upsellDivs = document.querySelectorAll('.main-upsell');
      let match = false;
      if (upsellDivs.length) {
        upsellDivs.forEach(div => {
          const cCode = div.getAttribute('data-val');
          if (cCode && cCode.includes(code)) {
            div.style.display = 'flex';
            const items = Array.from(div.querySelectorAll(".pro-title"));
            if (items.length) {
              const maxH = Math.max(...items.map(i => i.offsetHeight));
              items.forEach(i => i.style.height = `${maxH}px`);
            }
            match = true;
          } else {
            div.style.display = 'none';
          }
        });
        if (!match && upsellDivs[0]) upsellDivs[0].style.display = 'flex';
      }
    }, 100);
  }
});




// Footer Accordion (Desktop/Mobile hybrid)
if (document.querySelectorAll('.footer-row .singal-row').length > 0) {
  document.querySelectorAll('.singal-row .menu-title').forEach(title => {
    title.addEventListener('click', function () {
      this.classList.toggle('active');
      const next = this.nextElementSibling;
      if (next) next.style.display = this.classList.contains('active') ? 'block' : 'none';

      if (window.innerWidth > 767) {
        if (this.classList.contains('active') && next) {
          const height = next.offsetHeight;
          const wrapper = this.closest('.bottom-top-left');
          if (wrapper) wrapper.style.height = `${height}px`;
        } else {
          const wrapper = this.closest('.bottom-top-left');
          if (wrapper) wrapper.style.height = '0';
        }
        document.documentElement.scrollTop = document.documentElement.scrollHeight;
        document.body.scrollTop = document.body.scrollHeight;
      }

      // Close siblings
      const parent = this.closest('.singal-row').parentNode;
      if (parent) {
        parent.querySelectorAll('.singal-row').forEach(sib => {
          if (sib !== this.closest('.singal-row')) {
            const sibTitle = sib.querySelector('.menu-title');
            const sibMenu = sib.querySelector('.child-menu');
            if (sibTitle) sibTitle.classList.remove('active');
            if (sibMenu) sibMenu.style.display = 'none';
          }
        });
      }
    });
  });
}
